import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { captureException } from '@sentry/react'
import { useActiveTemporarySession } from '../TemporarySessionBanner/useActiveTemporarySession'
import { useExpectedSubdomainQuery } from './ExpectedSubdomain.hook'

type ExpectedSubdomain =
  | { status: 'resolving' }
  | { status: 'resolved'; subdomain: string }
  | { status: 'unresolvable' }

/**
 * Subdomain the actor has to be on.
 *
 * Inside a temporary Backoffice session that is the session organization's subdomain.
 * Otherwise it is the subdomain of the actor's own organization.
 */
export const useExpectedSubdomain = (): ExpectedSubdomain | undefined => {
  const { user, loading: isLoadingUser } = useBackofficeLoggedInUser()
  const { session, loading: isLoadingSession } = useActiveTemporarySession()
  const {
    data,
    loading: isLoadingExpectedSubdomain,
    error,
  } = useErrorHandlingQuery(useExpectedSubdomainQuery, {
    skip: !user,
    // The user is not blocked on failure, so report it to Sentry instead of
    // showing a toast they cannot act on.
    onError: error => {
      captureException(error, {
        extra: { function: 'useExpectedSubdomain' },
      })
    },
  })

  if (isLoadingExpectedSubdomain || isLoadingSession || isLoadingUser) {
    return { status: 'resolving' }
  }

  if (!user || error) {
    // If the query errors, we don't want the user to be stuck
    return undefined
  }

  const expectedOrganizationId =
    session?.permissions.organizationId || user.organizationId

  const subdomain = data?.backofficeOrganizations?.find(
    organization => organization.id === expectedOrganizationId,
  )?.subDomain

  return subdomain
    ? { status: 'resolved', subdomain }
    : { status: 'unresolvable' }
}
